'use client';

import { useAtomValue, useSetAtom } from 'jotai';

import { activeLeadFormAtom } from '@core/Atoms/Leads/ActiveLeadForm.atom';
import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { currentVehicleIdAtom } from '@core/Atoms/Vehicle/CurrentVehicleId.atom';
import type { LeadFormId } from '@core/Entities/Leads/LeadFormId';
import type { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import { getMakeModelLabelsFromAggregationsArray } from '@core/Utils/MakeModel/GetMakeModelLabelsFromAggregations';
import { ChevronRight } from '@gds/Icons/Paths/ChevronRight';
import { NewCar } from '@gds/Icons/Paths/NewCar';
import { experimentsAtom } from '@growthBookExperimentation/Atoms/Experiments.atom';
import { useDriftrockCheckbox } from '@lead-forms/Components/FormComponents/MultiLeadCheckboxes/OneClickDriftrockCheckbox/UseDriftrockCheckbox';
import { leadFormDataAtom } from '@lead-forms/Components/LeadFormData/LeadFormData.atom';
import { generateLeadTrackingData } from '@lead-forms/Tracking/LeadForms.tracking';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import { doesDriftRockModelMatchVehicle } from './TestDriveCta.utils';

import styles from './TestDriveCta.module.css';

type TestDriveCtaProps = {
  vehicle: Vehicle;
  strings: Record<string, string>;
  isReady: boolean;
};

export const TestDriveCta = ({ vehicle, strings, isReady }: TestDriveCtaProps) => {
  const aggregations = useAtomValue(aggregationsAtom);
  const toggleLeadForm = useSetAtom(activeLeadFormAtom);

  //tracking stuff
  const experiments = useAtomValue(experimentsAtom);
  const listingId = useAtomValue(currentVehicleIdAtom) || vehicle?.heycarId || null;
  const userId = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const formState = useAtomValue(formStateAtom);
  const formData = useAtomValue(leadFormDataAtom('message-dealer' as LeadFormId));

  let makeModelLabel = '';
  const product = useDriftrockCheckbox({ vehicle: vehicle, locale: 'uk' });
  if (!product) return null;

  const isSameModel = doesDriftRockModelMatchVehicle(vehicle, product);

  if (isSameModel) {
    const { make, model } = getMakeModelLabelsFromAggregationsArray(
      product.productTarget.make,
      product.productTarget.model,
      aggregations,
    );
    makeModelLabel = `${make?.toUpperCase()} ${model?.toUpperCase()}`;
  }

  const handleClick = () => {
    const {
      leadStart: { event, context },
    } = generateLeadTrackingData({
      searchParams,
      userId,
      formState,
      leadFormId: 'driftrock-exclusive',
      leadId: '',
      listingId,
      vehicle,
      ctaLocation: 'pdp',
      experiments,
      leadAction: '',
      financeQuoteChecked: formData.financeQuote.financeQuoteChecked || false,
    });

    trackCustomEvent({ event, context });
    toggleLeadForm('driftrock-exclusive');
  };

  return (
    <button
      className={styles.wrpr}
      data-test-id="test-drive-cta"
      onClick={handleClick}
      disabled={!isReady}
    >
      <NewCar />
      {isSameModel
        ? `${strings.testDriveSameModel} ${makeModelLabel}`
        : strings.testDriveDifferentModel}
      <ChevronRight />
    </button>
  );
};
